import { GsFormFieldColor, GsFormIf, GsFormIfArg, GsFormSchema } from 'libs/shared/src/components/gs-form/interfaces/gs-form-schema';
import { GsStructBorderRadius, GsStructBorderStyle, GsStructBorderWidth, GsStructButtonType, GsStructTarget } from '../../../models';
import { ACTIVE_SCHEMA_FIELDS } from './../basics/active.schema';
import { BASIC_SCHEMA_FIELDS } from './../basics/basic.schema';
import { borderSchemaFields } from './../basics/border.schema';
import { colorBackgroundGeneratorSchema, colorTextGeneratorSchema } from './../basics/colorSchema.schema';
import { HORIZONTAL_ALIGNMENT_SCHEMA } from './alignment.schema';
import { SPACING_SCHEMA } from './spacing.schema';

const IfNotLinkArg: GsFormIfArg = {
  formControlName: 'buttonType',
  value: GsStructButtonType.link,
  operator: '!=' // Show field when button type is not 'link'
};

/** Own style fields only show while the system button style is off ('!= true' also matches buttons saved without the flag) */
const IfOwnStyleArg: GsFormIfArg = {
  formControlName: 'useUiButtonSettings',
  value: true,
  operator: '!='
};

/** Hides a field while the system button style is on */
const IfOwnStyle: GsFormIf = { args: [IfOwnStyleArg] };

/**
 * The schema for the button component form.
 * Defines the fields to display for the form.
 */
export const BUTTON_COMPONENT_FORM_SCHEMA: GsFormSchema = {
  fields: [
    {
      formControlName: 'useUiButtonSettings',
      label: 'components.button.schema.useUiButtonSettings',
      description: 'components.button.schema.useUiButtonSettingsDescription',
      type: 'checkbox',
      default: false
    },
    ...ACTIVE_SCHEMA_FIELDS,
    ...BASIC_SCHEMA_FIELDS,
    ...HORIZONTAL_ALIGNMENT_SCHEMA,
    ...SPACING_SCHEMA,
    {
      formControlName: 'font',
      type: 'font',
      groupingName: 'components.button.schema.textOptions',
      label: 'components.button.schema.font',
      default: { family: 'Arial', weight: 400, style: 'normal', textTransform: 'none' },
      if: IfOwnStyle
    },
    {
      formControlName: 'textSize',
      type: 'number',
      groupingName: 'components.button.schema.textOptions',
      label: 'components.button.schema.textSize',
      isResponsive: true,
      default: { default: 16 },
      if: IfOwnStyle
    },
    {
      formControlName: 'buttonTextCaps',
      groupingName: 'components.button.schema.textOptions',
      label: 'components.button.schema.buttonTextCaps',
      type: 'checkbox',
      default: false,
      if: IfOwnStyle
    },
    {
      formControlName: 'buttonText',
      groupingName: 'components.button.schema.textOptions',
      label: 'components.button.schema.buttonText',
      type: 'text',
      default: 'Button'
    },
    {
      formControlName: 'buttonLinkUrlTarget',
      label: 'components.button.schema.buttonTarget',
      type: 'select',
      valueType: 'number',
      default: GsStructTarget._self,
      options: [
        { text: 'components.button.schema.sameTab', value: GsStructTarget._self },
        { text: 'components.button.schema.newTab', value: GsStructTarget._blank }
      ]
    },
    {
      formControlName: 'innerPadding',
      label: 'components.button.schema.innerPadding',
      type: 'padding',
      useTailwindClasses: true,
      isResponsive: true,
      default: { default: { top: 12, right: 24, bottom: 12, left: 24 } },
      if: IfOwnStyle
    },
    {
      formControlName: 'buttonType',
      label: 'components.button.schema.buttonType',
      description: 'components.button.schema.buttonTypeDescription',
      type: 'select',
      valueType: 'number',
      default: GsStructButtonType.solid,
      options: [
        { text: 'components.button.schema.solid', value: GsStructButtonType.solid },
        { text: 'components.button.schema.outline', value: GsStructButtonType.outline },
        { text: 'components.button.schema.ghost', value: GsStructButtonType.ghost },
        { text: 'components.button.schema.link', value: GsStructButtonType.link }
      ],
      if: IfOwnStyle
    },
    ...colorTextGeneratorSchema(
      'button',
      'components.button.schema.button',
      'primary-contrast',
      IfOwnStyle,
      'components.button.schema.defaultStyle'
    ),
    ...colorTextGeneratorSchema(
      'buttonHover',
      'components.button.schema.buttonHover',
      'primary-contrast',
      IfOwnStyle,
      'components.button.schema.interactionStyle'
    ),
    ...colorTextGeneratorSchema(
      'buttonActive',
      'components.button.schema.buttonActive',
      'primary-contrast',
      IfOwnStyle,
      'components.button.schema.interactionStyle'
    ),

    ...colorTextGeneratorSchema(
      'buttonDisabled',
      'components.button.schema.buttonDisabled',
      'medium-main',
      IfOwnStyle,
      'components.button.schema.interactionStyle'
    ),
    ...colorTextGeneratorSchema(
      'buttonFocus',
      'components.button.schema.buttonFocus',
      'primary-contrast',
      IfOwnStyle,
      'components.button.schema.interactionStyle'
    ),
    ...colorBackgroundGeneratorSchema(
      'button',
      'components.button.schema.buttonBackground',
      'primary-main',
      {
        args: [
          IfOwnStyleArg,
          IfNotLinkArg,
          {
            formControlName: 'buttonType',
            value: GsStructButtonType.outline,
            operator: '!=' // Also skip for outline buttons
          }
        ]
      },
      'components.button.schema.defaultStyle'
    ),
    ...colorBackgroundGeneratorSchema(
      'buttonHover',
      'components.button.schema.buttonHoverBackground',
      'primary-shade',
      { args: [IfOwnStyleArg, IfNotLinkArg] },
      'components.button.schema.interactionStyle'
    ),
    ...colorBackgroundGeneratorSchema(
      'buttonActive',
      'components.button.schema.buttonActiveBackground',
      'primary-shade',
      { args: [IfOwnStyleArg, IfNotLinkArg] },
      'components.button.schema.interactionStyle'
    ),
    ...colorBackgroundGeneratorSchema(
      'buttonDisabled',
      'components.button.schema.buttonDisabledBackground',
      'primary-shine',
      { args: [IfOwnStyleArg, IfNotLinkArg] },
      'components.button.schema.interactionStyle'
    ),
    ...colorBackgroundGeneratorSchema(
      'buttonFocus',
      'components.button.schema.buttonFocusBackground',
      'primary-shade',
      { args: [IfOwnStyleArg, IfNotLinkArg] },
      'components.button.schema.interactionStyle'
    ),
    // Border Settings - Default to a small border with same color as button background for solid buttons
    ...borderSchemaFields(
      undefined,
      {
        borderRadius: GsStructBorderRadius.sm,
        borderColor: 'primary-main',
        borderStyle: GsStructBorderStyle.solid,
        borderWidth: GsStructBorderWidth.twoPixels
      },
      undefined,
      IfOwnStyle
    ),
    {
      formControlName: 'borderHoverColor',
      label: 'schema.border.borderHoverColor',
      type: 'color',
      nullable: true,
      if: { args: [IfOwnStyleArg, { formControlName: 'borderStyle', value: GsStructBorderStyle.none, operator: '!=' }] },
      groupingName: 'schema.border.name'
    } as GsFormFieldColor
  ]
};
